import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Select } from '@/shared/components/Select';
import { Button } from '@/shared/components/Button';
import { ApiError } from '@/lib/api-client';
import { useOppositions } from '@/features/director-professors/hooks/useSubjects';
import { useShifts } from '@/features/director-schedules/hooks/useShifts';
import { useUpdateStudent } from '../hooks/useStudents';
/** Reassigns a student to a different opposition + shift. */
export function ChangeStudentShiftForm({ student, onSuccess }) {
    const [serverError, setServerError] = useState(null);
    const updateStudent = useUpdateStudent(student.id);
    const { data: oppositions = [], isLoading: oppLoading } = useOppositions();
    const enabledOppositions = oppositions.filter((o) => o.is_enabled);
    const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm({
        defaultValues: {
            opposition_id: student.opposition_id,
            shift_id: student.shift_id,
        },
    });
    const oppositionId = watch('opposition_id');
    const shiftId = watch('shift_id');
    const { data: shifts = [], isLoading: shiftsLoading } = useShifts(oppositionId || null);
    // Reset shift when opposition changes (unless we're still on the original
    // opposition, in which case keep the original shift).
    useEffect(() => {
        if (oppositionId !== student.opposition_id) {
            setValue('shift_id', '');
        }
    }, [oppositionId, student.opposition_id, setValue]);
    function onSubmit(values) {
        setServerError(null);
        if (!values.opposition_id || !values.shift_id) {
            setServerError('Selecciona oposición y turno.');
            return;
        }
        if (values.opposition_id === student.opposition_id &&
            values.shift_id === student.shift_id) {
            onSuccess();
            return;
        }
        updateStudent.mutate({ opposition_id: values.opposition_id, shift_id: values.shift_id }, {
            onSuccess: () => onSuccess(),
            onError: (err) => {
                setServerError(err instanceof ApiError ? err.message : 'Ha ocurrido un error.');
            },
        });
    }
    const shiftPlaceholder = !oppositionId
        ? 'Selecciona oposición primero'
        : shiftsLoading
            ? 'Cargando…'
            : shifts.length === 0
                ? 'No hay turnos en esta oposición'
                : 'Seleccionar turno';
    return (_jsxs("form", { onSubmit: handleSubmit(onSubmit), className: "flex flex-col gap-4", noValidate: true, children: [_jsxs("p", { className: "text-sm", style: { color: 'var(--fg-2)' }, children: ["Asignaci\u00F3n actual: ", _jsx("strong", { style: { color: 'var(--fg)' }, children: student.opposition_name }), " \u00B7 ", student.shift_name, "."] }), _jsx(Select, { label: "Oposici\u00F3n", disabled: oppLoading, options: [
                    { value: '', label: oppLoading ? 'Cargando…' : 'Seleccionar oposición' },
                    ...enabledOppositions.map((o) => ({ value: o.id, label: o.name })),
                ], error: errors.opposition_id?.message, ...register('opposition_id', { required: 'Selecciona una oposición' }) }), _jsx(Select, { label: "Turno", disabled: !oppositionId || shiftsLoading, options: [
                    { value: '', label: shiftPlaceholder },
                    ...shifts.map((s) => ({ value: s.id, label: s.name })),
                ], error: errors.shift_id?.message, ...register('shift_id', { required: 'Selecciona un turno' }) }), serverError && (_jsx("p", { role: "alert", className: "text-sm text-red-600 dark:text-red-400", children: serverError })), _jsx(Button, { type: "submit", loading: updateStudent.isPending, disabled: !oppositionId || !shiftId, className: "w-full", children: "Guardar cambios" })] }));
}
//# sourceMappingURL=ChangeStudentShiftForm.js.map